import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, catchError, map, of, switchMap, withLatestFrom } from 'rxjs';
import {
    dummyAction,
    loadAircraftDDLAction,
    loadAirlineDDLAction,
    loadAirPortDDLAction,
    loadCabinDDLAction,
    loadCountriesDDLAction,
    loadDropdownListData,
} from '../actions/common.action';
import {
    selectAirCraft,
    selectAirlineDDL,
    selectAirPortDDL,
    selectCabinDDL,
    selectCountriesDDL,
} from '../selectors/common.selector';
import { DropdownService } from '@services/dropdown.service';
import { StoreModel } from '@models/store.model';
import { AirportsGroup, CountryDDLModel, DropDownListCodeResponse } from '@models/dropdown-list.model';
import { BaseResponseModel } from '@models/app.model';
import { AirCraftModel } from '@models/air-craft.model';
import { BookingService } from '@services/booking.service';

@Injectable()
export class MasterDataEffects {
    private actions$ = inject(Actions);
    private store = inject(Store<StoreModel>);
    private dropdownService = inject(DropdownService);
    private bookingService = inject(BookingService);

    loadCabinDDL$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadCabinDDLAction),
            withLatestFrom(this.store.select(selectCabinDDL)),
            switchMap(([_, cabin]) => {
                if (!cabin.length) {
                    return this.dropdownService.getCabinDDL().pipe(
                        map((data: BaseResponseModel<DropDownListCodeResponse[]>) =>
                            loadDropdownListData({ data: data.Data, key: 'cabinDDL' })
                        ),
                        catchError(() => EMPTY)
                    );
                }
                return of(dummyAction());
            })
        );
    });

    loadAirPortDDL$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadAirPortDDLAction),
            withLatestFrom(this.store.select(selectAirPortDDL)),
            switchMap(([_, airPort]) => {
                if (!airPort.length) {
                    return this.dropdownService.getAirPortDDL().pipe(
                        map((data: BaseResponseModel<AirportsGroup[]>) =>
                            loadDropdownListData({ data: data.Data, key: 'airPortDDL' })
                        ),
                        catchError(() => EMPTY)
                    );
                }
                return of(dummyAction());
            })
        );
    });

    loadAirlineDDL$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadAirlineDDLAction),
            withLatestFrom(this.store.select(selectAirlineDDL)),
            switchMap(([_, airline]) => {
                if (!airline.length) {
                    return this.dropdownService.getAirlineDDL().pipe(
                        map((data: BaseResponseModel<DropDownListCodeResponse[]>) =>
                            loadDropdownListData({ data: data.Data, key: 'airlineDDL' })
                        ),
                        catchError(() => EMPTY)
                    );
                }
                return of(dummyAction());
            })
        );
    });

    loadCountriesDDL$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadCountriesDDLAction),
            withLatestFrom(this.store.select(selectCountriesDDL)),
            switchMap(([_, countries]) => {
                if (!countries.length) {
                    return this.dropdownService.getCountriesDDL().pipe(
                        map((data: BaseResponseModel<CountryDDLModel[]>) =>
                            loadDropdownListData({ data: data.Data, key: 'countriesDDL' })
                        ),
                        catchError(() => EMPTY)
                    );
                }
                return of(dummyAction());
            })
        );
    });

    loadAirCrapDDL$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(loadAircraftDDLAction),
            withLatestFrom(this.store.select(selectAirCraft)),
            switchMap(([_, airCraps]) => {
                if (!airCraps.length) {
                    return this.bookingService.getAllAircraft().pipe(
                        map((data: BaseResponseModel<AirCraftModel[]>) =>
                            loadDropdownListData({ data: data.Data, key: 'airCrafts' })
                        ),
                        catchError(() => EMPTY)
                    );
                }
                return of(dummyAction());
            })
        );
    });
}
